import React, { Fragment, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import Avatar from '@atlaskit/avatar';
import Form, { Field } from '@atlaskit/form';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives';
import type { OptionProps, SingleValueProps, ValueType } from '@atlaskit/select';
import Select, { components } from '@atlaskit/select';

import { SidePanel } from '@confluence/side-panel';

import {
	useAiBulkNavigationCurrentPage,
	useAiBulkNavigationGotoPage,
} from './AiBulkNavigationContextProvider';
import { BulkConfigureFooter } from './BulkConfigureFooter';
import { useBulkCreateContext } from './BulkCreateContextProvider';
import { BulkCreateNavigationHeader } from './BulkCreateNavigationHeader';
import { headerHeight } from './IssueCreateSidePanel';
import { messages } from './messages';

interface Option {
	label: string;
	value: string;
	iconUrl?: string;
}

interface Category {
	site?: ValueType<Option>;
	project?: ValueType<Option>;
	defaultIssueType?: ValueType<Option>;
}

// This is the view for both AI and non-AI bulk create where users can edit and create individual issues
// Look into if using the Form component just complicates things or if it's necessary - https://atlassian.design/components/form/examples#select
export const BulkConfigureView = () => {
	const { formatMessage } = useIntl();
	const goToPage = useAiBulkNavigationGotoPage();
	const currentPage = useAiBulkNavigationCurrentPage();

	const onClose = useCallback(() => void goToPage('default'), [goToPage]);
	const isOpen = currentPage === 'configure';

	const [, { setCurrentJiraSite, setCurrentJiraProject }] = useBulkCreateContext();

	const sites = [{ label: 'Tasty Pet Snacks', value: 'Tasty Pet Snacks' }];
	const projects = [
		{
			label: 'Marketing',
			value: 'Marketing',
			iconUrl:
				'https://product-fabric.atlassian.net/rest/api/2/universal_avatar/view/type/issuetype/avatar/10315',
		},
	];
	const defaultIssueTypes = [{ label: 'Task', value: 'Task' }];

	const ProjectOrIssueTypeOption = (props: OptionProps<Option>) => (
		<components.Option {...props}>
			<Inline space="space.100" alignBlock="center">
				<Avatar size="small" src={props.data.iconUrl} />
				<Text size="medium">{props.label}</Text>
			</Inline>
		</components.Option>
	);

	const ProjectOrIssueTypeSingleValue = (props: SingleValueProps<Option>) => (
		<components.SingleValue {...props}>
			<Inline space="space.100" alignBlock="center">
				<Avatar size="small" src={props.data.iconUrl} />
				<Text size="medium">{props.data.label}</Text>
			</Inline>
		</components.SingleValue>
	);

	const onSubmit = (data: Category) => {
		void setCurrentJiraSite(data.site?.label);
		void setCurrentJiraProject(data.project?.label);
		onClose();
	};

	return (
		<SidePanel isOpen={isOpen} isFixed headerHeight={headerHeight} panelTop={headerHeight}>
			<Box padding="space.300">
				<Stack space="space.100">
					<BulkCreateNavigationHeader onClose={onClose} />
					<Text color="color.text.subtlest">
						<FormattedMessage {...messages.bulkCreateConfigureBodyText} />
					</Text>
					<Form<Category> onSubmit={onSubmit}>
						{({ formProps }) => (
							<form id="configureForm" {...formProps}>
								<Field<ValueType<Option>>
									name="sites"
									label={formatMessage(messages.bulkConfigureFormSite)}
									defaultValue={sites[0]}
								>
									{({ fieldProps: { id, ...rest } }) => (
										<Fragment>
											<Select<Option> inputId={id} {...rest} options={sites} />
										</Fragment>
									)}
								</Field>
								<Field<ValueType<Option>>
									name="projects"
									label={formatMessage(messages.bulkConfigureFormProject)}
									defaultValue={projects[0]}
								>
									{({ fieldProps: { id, ...rest } }) => (
										<Fragment>
											<Select<Option>
												inputId={id}
												{...rest}
												options={projects}
												components={{
													Option: ProjectOrIssueTypeOption,
													SingleValue: ProjectOrIssueTypeSingleValue,
												}}
											/>
										</Fragment>
									)}
								</Field>
								<Field<ValueType<Option>>
									name="defaultIssueTypes"
									label={formatMessage(messages.bulkConfigureFormDefaultIssueType)}
									defaultValue={defaultIssueTypes[0]}
								>
									{({ fieldProps: { id, ...rest } }) => (
										<Fragment>
											<Select<Option> inputId={id} {...rest} options={defaultIssueTypes} />
										</Fragment>
									)}
								</Field>
							</form>
						)}
					</Form>
				</Stack>
			</Box>
			<BulkConfigureFooter onClose={onClose} />
		</SidePanel>
	);
};
